import { cn } from "app/lib/utils"
import { getVolumeIcon, type PlayerVolumeColor } from "app/player/getVolumeIcon"
import { motion } from "framer-motion"
import { useCallback, useEffect, useId, useRef, useState } from "react"
import { poppins } from "theme/fonts"
import PlayerIcon from "./PlayerIcon"
import PlayerVolumeTrack from "./PlayerVolumeTrack"
import {
  containerVariants,
  grayVolumeInnerMutedActiveClassName,
  grayVolumeMutedActiveClassName,
  grayVolumeTriggerClassName,
  labelVariants,
  playerIconButtonInnerClassName,
  VERTICAL_COLLAPSED_HEIGHT,
  VERTICAL_EXPANDED_HEIGHT,
  VOLUME_COLLAPSE_DURATION,
} from "./playerVolumeStyles"
import { useCloseOnOutsidePointerDown } from "./useCloseOnOutsidePointerDown"
import { usePlayerVolumeMute } from "./usePlayerVolumeMute"

type Props = {
  volume: number
  onVolumeChange: (volume: number) => void
  color?: PlayerVolumeColor
  showMuteButton?: boolean
  /** iOS: volume slider is inert; show mute toggle only. */
  muteOnly?: boolean
}

function CloseIcon() {
  return (
    <PlayerIcon src="/icons/player/thick/dark/ico_close_thin.svg" size={24} />
  )
}

function VerticalVolumeSlider({
  color,
  volume,
  onVolumeChange,
  showMuteButton,
  onToggleMute,
}: {
  color: PlayerVolumeColor
  volume: number
  onVolumeChange: (volume: number) => void
  showMuteButton?: boolean
  onToggleMute: () => void
}) {
  const volumePercent = Math.round(volume * 100)

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-[9px] pt-[9px] mr-[1px] w-[46px]",
        poppins.className
      )}
    >
      <span
        className={cn(
          labelVariants({ color }),
          volumePercent < 100 && "mr-[1px]"
        )}
      >
        {volumePercent}
      </span>
      <PlayerVolumeTrack
        volume={volume}
        onVolumeValueChange={onVolumeChange}
        color={color}
        orientation="vertical"
      />
      {showMuteButton ? (
        <button
          type="button"
          className={cn(
            "flex size-10 items-center justify-center rounded-[8px] mb-[4px]",
            volume === 0 && "bg-[rgba(255,65,0,0.1)]"
          )}
          aria-label={volume === 0 ? "Unmute" : "Mute"}
          aria-pressed={volume === 0}
          onClick={onToggleMute}
        >
          <PlayerIcon
            src={getVolumeIcon(color, 0, { active: volume === 0 })}
            size={24}
          />
        </button>
      ) : null}
    </div>
  )
}

export default function PlayerVerticalVolumeControl({
  volume,
  onVolumeChange,
  color = "blue",
  showMuteButton = false,
  muteOnly = false,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  const collapsibleId = useId()
  const [collapsedExpanded, setCollapsedExpanded] = useState(false)
  const [sliderMounted, setSliderMounted] = useState(false)
  const { toggleMute, isMuted } = usePlayerVolumeMute(volume, onVolumeChange)

  const closePanel = useCallback(() => {
    setCollapsedExpanded(false)
  }, [])

  useCloseOnOutsidePointerDown(rootRef, collapsedExpanded, closePanel)

  useEffect(() => {
    if (collapsedExpanded) {
      setSliderMounted(true)
    }
  }, [collapsedExpanded])

  if (muteOnly) {
    // grayVolumeTrigger uses w-full; pin to the same 48×48 frame as the collapsible control.
    return (
      <div className="h-12 w-12 shrink-0">
        <button
          type="button"
          className={cn(
            grayVolumeTriggerClassName,
            isMuted && grayVolumeMutedActiveClassName
          )}
          aria-label={isMuted ? "Unmute" : "Mute"}
          aria-pressed={isMuted}
          onClick={(event) => {
            event.stopPropagation()
            toggleMute()
          }}
        >
          <span
            className={cn(
              playerIconButtonInnerClassName,
              isMuted && grayVolumeInnerMutedActiveClassName
            )}
          >
            <PlayerIcon
              src={getVolumeIcon(color, volume, { active: isMuted })}
              size={24}
            />
          </span>
        </button>
      </div>
    )
  }

  const isPanelOpen = collapsedExpanded || sliderMounted

  return (
    <div
      ref={rootRef}
      className="relative z-20 h-12 w-12 shrink-0 overflow-visible"
    >
      <motion.div
        className={cn(
          "absolute bottom-0 left-0 w-12",
          isPanelOpen ? "overflow-hidden" : "overflow-visible",
          isPanelOpen && containerVariants({ color, orientation: "vertical" })
        )}
        animate={{
          height: collapsedExpanded
            ? VERTICAL_EXPANDED_HEIGHT
            : VERTICAL_COLLAPSED_HEIGHT,
        }}
        transition={{ duration: VOLUME_COLLAPSE_DURATION, ease: "easeOut" }}
        onAnimationComplete={() => {
          if (!collapsedExpanded) {
            setSliderMounted(false)
          }
        }}
      >
        <div className="absolute bottom-0 left-0 flex w-12 flex-col mr-[1px]">
          {sliderMounted ? (
            <div className="w-full" id={collapsibleId}>
              <VerticalVolumeSlider
                color={color}
                volume={volume}
                onVolumeChange={onVolumeChange}
                showMuteButton={showMuteButton}
                onToggleMute={toggleMute}
              />
            </div>
          ) : null}
          <div
            className={cn(
              "flex shrink-0 flex-col items-center",
              isPanelOpen && "bg-white"
            )}
          >
            {isPanelOpen ? (
              <span className="h-px w-10 bg-black/20" aria-hidden />
            ) : null}
            <button
              type="button"
              className={cn(
                isPanelOpen
                  ? "flex size-12 w-full items-center justify-center rounded-b-[12px] text-neutraal-900 mr-[2px]"
                  : cn(
                      grayVolumeTriggerClassName,
                      isMuted && grayVolumeMutedActiveClassName
                    )
              )}
              aria-label={isPanelOpen ? "Close volume control" : "Volume"}
              aria-pressed={!isPanelOpen && isMuted}
              aria-expanded={isPanelOpen}
              aria-controls={collapsibleId}
              onClick={() => {
                if (isPanelOpen) {
                  closePanel()
                  return
                }
                setSliderMounted(true)
                setCollapsedExpanded(true)
              }}
            >
              {isPanelOpen ? (
                <CloseIcon />
              ) : (
                <span
                  className={cn(
                    playerIconButtonInnerClassName,
                    isMuted && grayVolumeInnerMutedActiveClassName
                  )}
                >
                  <PlayerIcon
                    src={getVolumeIcon(color, volume, { active: isMuted })}
                    size={24}
                  />
                </span>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
